/**
 * Datalogger HTTP helpers. In development, fetches go to same-origin /datalogger/...
 * (see src/setupProxy.js) with X-Datalogger-Target for the real device origin.
 */

const DEV_PROXY_PREFIX = '/datalogger';
const DATALOGGER_TARGET_HEADER = 'X-Datalogger-Target';

function isPrivateNetworkHostname(hostname) {
  const h = String(hostname || '').toLowerCase();
  if (!h) return false;
  if (h === 'localhost' || h === '127.0.0.1') return true;
  if (h.endsWith('.local')) return true;
  const parts = h.split('.');
  if (parts.length !== 4) return false;
  const nums = parts.map((p) => Number(p));
  if (nums.some((n) => !Number.isFinite(n) || n !== (n | 0) || n < 0 || n > 255)) return false;
  const [a, b] = nums;
  if (a === 10) return true;
  if (a === 172 && b >= 16 && b <= 31) return true;
  if (a === 192 && b === 168) return true;
  if (a === 169 && b === 254) return true;
  return false;
}

function isElectronLocalhost() {
  if (typeof window === 'undefined') return false;
  if (window.electronAPI?.isElectron !== true) return false;
  const host = window.location?.hostname;
  return host === 'localhost' || host === '127.0.0.1';
}

function shouldUseDevDataloggerProxy(resolvedOrigin) {
  if (process.env.REACT_APP_DATALOGGER_NO_PROXY === '1') return false;
  if (process.env.NODE_ENV !== 'development' && !isElectronLocalhost()) return false;
  try {
    const { hostname, protocol } = new URL(resolvedOrigin);
    if (protocol !== 'http:') return false;
    return isPrivateNetworkHostname(hostname);
  } catch {
    return false;
  }
}

/**
 * @param {string} espUrl
 * @returns {boolean}
 */
export function isDataloggerDevProxyActive(espUrl) {
  const o = resolveDataloggerOrigin(espUrl);
  if (!o) return false;
  return shouldUseDevDataloggerProxy(o) && typeof window !== 'undefined';
}

/**
 * @param {string | null} resolvedOrigin
 * @returns {string | null}
 */
export function getDataloggerFetchBase(resolvedOrigin) {
  if (!resolvedOrigin) return null;
  if (typeof window === 'undefined') return resolvedOrigin;
  // HTTPS pages cannot fetch plain HTTP logger endpoints (mixed content is blocked by browsers).
  try {
    const pageProtocol = window.location?.protocol;
    const targetProtocol = new URL(resolvedOrigin).protocol;
    if (pageProtocol === 'https:' && targetProtocol === 'http:') return null;
  } catch {
    // Fall through to existing behavior when URL parsing fails.
  }
  if (!shouldUseDevDataloggerProxy(resolvedOrigin)) return resolvedOrigin;
  return `${window.location.origin}${DEV_PROXY_PREFIX}`;
}

/**
 * @param {string} logicalOrigin
 * @param {Record<string, string>} [baseHeaders]
 * @returns {Record<string, string>}
 */
function withDataloggerTargetHeader(logicalOrigin, baseHeaders = {}) {
  if (!logicalOrigin || !shouldUseDevDataloggerProxy(logicalOrigin)) return { ...baseHeaders };
  return {
    ...baseHeaders,
    [DATALOGGER_TARGET_HEADER]: logicalOrigin,
  };
}

/**
 * @param {string} url
 * @param {{ timeoutMs?: number, logicalLoggerOrigin?: string | null, [k: string]: unknown }} [opts]
 */
function fetchWithTimeout(url, { timeoutMs = 10000, logicalLoggerOrigin, ...init } = {}) {
  const controller = new AbortController();
  const tid = setTimeout(() => controller.abort(), timeoutMs);
  const { signal: _s, headers: inHeaders, ...rest } = init || {};
  return fetch(url, {
    cache: 'no-store',
    ...rest,
    signal: controller.signal,
    headers: withDataloggerTargetHeader(
      logicalLoggerOrigin || null,
      inHeaders && typeof inHeaders === 'object' && !Array.isArray(inHeaders) ? { ...inHeaders } : {}
    ),
  }).finally(() => clearTimeout(tid));
}

/**
 * Read response body with a wall-clock limit (fetch() resolves at headers; slow ESP bodies
 * would otherwise hang indefinitely).
 * @param {Response} resp
 * @param {{ onProgress?: (p: { receivedBytes: number, totalBytes?: number }) => void, maxReadMs?: number }} [opts]
 */
async function readResponseBodyAsTextWithProgress(resp, opts = {}) {
  const { onProgress, maxReadMs = 180000 } = opts;
  const rawLen = resp.headers.get('Content-Length');
  const totalBytes =
    rawLen && /^\d+$/.test(String(rawLen).trim()) ? Number(String(rawLen).trim()) : undefined;

  if (!resp.body || typeof resp.body.getReader !== 'function') {
    const text = await resp.text();
    const approx = new Blob([text]).size;
    onProgress?.({ receivedBytes: approx, totalBytes: totalBytes ?? approx });
    return text;
  }

  const reader = resp.body.getReader();
  const chunks = [];
  let receivedBytes = 0;
  const deadline = Date.now() + maxReadMs;

  for (;;) {
    const remain = deadline - Date.now();
    if (remain <= 0) {
      try {
        await reader.cancel();
      } catch (_e) {
        // ignore
      }
      throw new Error('Datalogger download read timed out');
    }
    let stallTimer;
    const stall = new Promise((_, rej) => {
      stallTimer = setTimeout(() => rej(new Error('Datalogger download read stalled')), remain);
    });
    let readResult;
    try {
      readResult = await Promise.race([reader.read(), stall]);
    } catch (e) {
      clearTimeout(stallTimer);
      try {
        await reader.cancel();
      } catch (_c) {
        // ignore
      }
      throw e;
    }
    clearTimeout(stallTimer);
    const { done, value } = readResult;
    if (done) break;
    if (value?.length) {
      chunks.push(value);
      receivedBytes += value.length;
      onProgress?.({ receivedBytes, totalBytes });
    }
  }

  const blob = new Blob(chunks);
  const text = await blob.text();
  onProgress?.({ receivedBytes: text.length, totalBytes: totalBytes ?? text.length });
  return text;
}


async function readResponseBodyAsArrayBufferWithProgress(resp, opts = {}) {
  const { onProgress } = opts;
  const rawLen = resp.headers.get('Content-Length');
  const totalBytes = rawLen && /^\d+$/.test(String(rawLen).trim()) ? Number(String(rawLen).trim()) : undefined;
  const buffer = await resp.arrayBuffer();
  onProgress?.({ receivedBytes: buffer.byteLength, totalBytes: totalBytes ?? buffer.byteLength });
  return buffer;
}

export function isShokiqBinaryFilename(fileName) {
  return String(fileName || '').toLowerCase().endsWith('.shq');
}

export function decodeShokiqBinaryToCsv(buffer) {
  if (!buffer || buffer.byteLength < 32) return '';
  const view = new DataView(buffer);
  const magic = String.fromCharCode(view.getUint8(0), view.getUint8(1), view.getUint8(2), view.getUint8(3));
  if (magic !== 'SHQ1') return '';
  const recordSize = view.getUint8(5);
  if (recordSize < 48) return '';

  const headers = [
    'count', 'time', 'front_disp', 'rear_disp', 'gps_long', 'gps_lat', 'gps_speed', 'flag',
    'acc_frame_x', 'acc_frame_y', 'acc_frame_z', 'gyro_frame_x', 'gyro_frame_y', 'gyro_frame_z',
    'acc_bar_x', 'acc_bar_y', 'acc_bar_z', 'gyro_bar_x', 'gyro_bar_y', 'gyro_bar_z', 'micros',
  ];
  const lines = [headers.join(',')];
  let offset = 32;
  let firstMicros = null;
  while (offset + recordSize <= buffer.byteLength) {
    const count = view.getUint32(offset + 0, true);
    const micros = view.getUint32(offset + 4, true);
    if (firstMicros == null) firstMicros = micros;
    const time = ((micros - firstMicros) / 1000000).toFixed(4);
    const front = view.getUint16(offset + 8, true);
    const rear = view.getUint16(offset + 10, true);
    const lon = (view.getInt32(offset + 12, true) / 10000000).toFixed(7);
    const lat = (view.getInt32(offset + 16, true) / 10000000).toFixed(7);
    const speed = (view.getUint16(offset + 20, true) / 100).toFixed(2);
    const flag = view.getUint8(offset + 22);
    const accFrameX = (view.getInt16(offset + 24, true) / 1000).toFixed(3);
    const accFrameY = (view.getInt16(offset + 26, true) / 1000).toFixed(3);
    const accFrameZ = (view.getInt16(offset + 28, true) / 1000).toFixed(3);
    const gyroFrameX = (view.getInt16(offset + 30, true) / 100).toFixed(2);
    const gyroFrameY = (view.getInt16(offset + 32, true) / 100).toFixed(2);
    const gyroFrameZ = (view.getInt16(offset + 34, true) / 100).toFixed(2);
    const accBarX = (view.getInt16(offset + 36, true) / 1000).toFixed(3);
    const accBarY = (view.getInt16(offset + 38, true) / 1000).toFixed(3);
    const accBarZ = (view.getInt16(offset + 40, true) / 1000).toFixed(3);
    const gyroBarX = (view.getInt16(offset + 42, true) / 100).toFixed(2);
    const gyroBarY = (view.getInt16(offset + 44, true) / 100).toFixed(2);
    const gyroBarZ = (view.getInt16(offset + 46, true) / 100).toFixed(2);
    lines.push([
      count, time, front, rear, lon, lat, speed, flag,
      accFrameX, accFrameY, accFrameZ, gyroFrameX, gyroFrameY, gyroFrameZ,
      accBarX, accBarY, accBarZ, gyroBarX, gyroBarY, gyroBarZ, micros,
    ].join(','));
    offset += recordSize;
  }
  return lines.length > 1 ? lines.join('\n') : '';
}

/**
 * @param {string} espUrl
 * @returns {string | null} Origin (e.g. http://192.168.4.1)
 */
export function resolveDataloggerOrigin(espUrl) {
  const raw = String(espUrl || '').trim();
  if (!raw) return null;
  try {
    const withScheme = /^https?:\/\//i.test(raw) ? raw : `http://${raw}`;
    return new URL(withScheme).origin;
  } catch {
    return null;
  }
}

/**
 * @param {string} resolvedOrigin
 * @returns {Promise<boolean>}
 */
export async function pingDatalogger(resolvedOrigin, { timeoutMs = 4000 } = {}) {
  const base = getDataloggerFetchBase(resolvedOrigin);
  if (!base) return false;
  const candidates = [`${base}/`, base, `${base}/download`];
  for (const url of candidates) {
    try {
      const resp = await fetchWithTimeout(url, { timeoutMs, logicalLoggerOrigin: resolvedOrigin });
      if (resp.type === 'opaque') return false;
      if (resp.ok || resp.status === 404 || resp.status === 401 || resp.status === 403) return true;
    } catch {
      // try next
    }
  }
  return false;
}

/**
 * Find data_*.csv names in the logger web UI HTML (Suspension Logger “Saved files” list).
 * @param {string} html
 * @returns {string[]}
 */
export function extractDataCsvFilenamesFromHtml(html) {
  if (!html || typeof html !== 'string') return [];
  const found = new Set();
  if (/\bdata\.csv\b/i.test(html) || /download\?file=data\.csv/i.test(html)) {
    found.add('data.csv');
  }
  const re = /data[-_]\d+[-_]\d+\.(csv|shq)|data_\d+\.csv/gi;
  let m;
  for (;;) {
    m = re.exec(html);
    if (!m) break;
    const name = m[0];
    if (name) found.add(name);
  }
  return Array.from(found).sort((a, b) => a.localeCompare(b, undefined, { numeric: true }));
}

function normalizeApiFilesJson(json) {
  if (Array.isArray(json)) return json;
  if (json && Array.isArray(json.files)) return json.files;
  if (json && Array.isArray(json.data)) return json.data;
  if (json && Array.isArray(json.list)) return json.list;
  return [];
}

async function tryListFromApi(logicalOrigin, fetchBase) {
  try {
    const listResp = await fetchWithTimeout(`${fetchBase}/api/files`, { timeoutMs: 8000, logicalLoggerOrigin: logicalOrigin });
    if (!listResp.ok) return [];
    const json = await listResp.json();
    const list = normalizeApiFilesJson(json);
    return list
      .map((f) => {
        if (typeof f === 'string') return f.replace(/^\//, '');
        return String(f?.name || f?.path || f?.file || '').replace(/^\//, '');
      })
      .filter((n) => n && /\.(csv|shq)$/i.test(n));
  } catch {
    return [];
  }
}

async function tryListFromIndexHtml(logicalOrigin, fetchBase) {
  try {
    const r = await fetchWithTimeout(`${fetchBase}/`, { timeoutMs: 10000, logicalLoggerOrigin: logicalOrigin });
    if (!r.ok) return [];
    const html = await r.text();
    return extractDataCsvFilenamesFromHtml(html);
  } catch {
    return [];
  }
}

/**
 * True if GET /download?file=… returns 2xx. Cancels the body to avoid reading large CSVs when only checking existence.
 * @param {string} logicalOrigin
 * @param {string} fetchBase
 * @param {string} fileName
 */
async function downloadUrlRespondsOk(logicalOrigin, fetchBase, fileName) {
  const url = `${fetchBase}/download?file=${encodeURIComponent(fileName)}`;
  try {
    const resp = await fetchWithTimeout(url, { timeoutMs: 8000, logicalLoggerOrigin: logicalOrigin });
    if (!resp.ok) return false;
    const body = resp.body;
    if (body && typeof body.cancel === 'function') {
      await body.cancel();
    } else {
      await resp.text();
    }
    return true;
  } catch {
    return false;
  }
}

/**
 * When the device has no /api/files and the index page does not list CSVs in the HTML, discover names by probing
 * /download?file=data.csv and data_000.csv … (same window as {@link probeDataSeries}).
 * @param {string} logicalOrigin
 * @param {string} fetchBase
 * @returns {Promise<string[]>}
 */
async function listFilenamesByDownloadProbe(logicalOrigin, fetchBase) {
  const out = [];
  if (await downloadUrlRespondsOk(logicalOrigin, fetchBase, 'data.csv')) {
    out.push('data.csv');
  }
  let missesSinceOk = 0;
  const maxAfterFound = 5;
  const maxMissesBeforeAnyFound = 40;
  const maxIndex = 199;
  let foundAnyNumbered = false;
  for (let i = 0; i <= maxIndex; i += 1) {
    const fileName = `data_${String(i).padStart(3, '0')}.csv`;
    const ok = await downloadUrlRespondsOk(logicalOrigin, fetchBase, fileName);
    if (ok) {
      out.push(fileName);
      foundAnyNumbered = true;
      missesSinceOk = 0;
    } else {
      missesSinceOk += 1;
      if (foundAnyNumbered && missesSinceOk >= maxAfterFound) break;
      if (!foundAnyNumbered && missesSinceOk >= maxMissesBeforeAnyFound) break;
    }
  }
  return out;
}

function sortDataloggerFilenames(names) {
  return [...new Set(names)].sort((a, b) => {
    if (a === 'data.csv' && b !== 'data.csv') return -1;
    if (b === 'data.csv' && a !== 'data.csv') return 1;
    return a.localeCompare(b, undefined, { numeric: true });
  });
}

/**
 * @param {string} resolvedOrigin logical
 * @returns {Promise<string[]>}
 */
export async function listDataloggerCsvFilenames(resolvedOrigin) {
  if (!resolvedOrigin) return [];
  const fetchBase = getDataloggerFetchBase(resolvedOrigin);
  const api = await tryListFromApi(resolvedOrigin, fetchBase);
  if (api.length) {
    return sortDataloggerFilenames(api);
  }
  const fromHtml = await tryListFromIndexHtml(resolvedOrigin, fetchBase);
  if (fromHtml.length) {
    return sortDataloggerFilenames(fromHtml);
  }
  const probed = await listFilenamesByDownloadProbe(resolvedOrigin, fetchBase);
  return sortDataloggerFilenames(probed);
}

/**
 * @param {string} logicalOrigin
 * @param {string} fetchBase
 * @param {string} fileName
 * @param {{
 *   onDownloadProgress?: (p: { receivedBytes: number, totalBytes?: number }) => void,
 *   headersTimeoutMs?: number,
 *   maxReadMs?: number,
 * }} [options]
 * @returns {Promise<string | null>} raw file text, or null
 */
export async function downloadDataloggerCsvText(logicalOrigin, fetchBase, fileName, options = {}) {
  const {
    onDownloadProgress,
    headersTimeoutMs = 30000,
    maxReadMs = 180000,
  } = options;
  const url = `${fetchBase}/download?file=${encodeURIComponent(fileName)}`;
  try {
    const resp = await fetchWithTimeout(url, {
      timeoutMs: headersTimeoutMs,
      logicalLoggerOrigin: logicalOrigin,
    });
    if (!resp.ok) return null;
    if (isShokiqBinaryFilename(fileName)) {
      const buffer = await readResponseBodyAsArrayBufferWithProgress(resp, {
        onProgress: onDownloadProgress,
        maxReadMs,
      });
      return decodeShokiqBinaryToCsv(buffer);
    }
    return await readResponseBodyAsTextWithProgress(resp, {
      onProgress: onDownloadProgress,
      maxReadMs,
    });
  } catch {
    return null;
  }
}

/**
 * @param {string} logicalOrigin
 * @param {string} fetchBase
 * @param {string} fileName
 * @param {(t: string) => unknown[]} parseCsv
 * @returns {Promise<{ fileName: string, text: string } | null>}
 */
async function fetchCsvIfValid(logicalOrigin, fetchBase, fileName, parseCsv) {
  const text = await downloadDataloggerCsvText(logicalOrigin, fetchBase, fileName);
  if (!text) return null;
  const rows = parseCsv(text);
  if (!rows?.length) return null;
  return { fileName, text };
}

async function trySingleDataCsv(logicalOrigin, fetchBase, parseCsv) {
  return fetchCsvIfValid(logicalOrigin, fetchBase, 'data.csv', parseCsv);
}

/**
 * @param {string} logicalOrigin
 * @param {string} origin fetch base
 * @param {(t: string) => unknown[]} parseCsvTelemetryText
 * @returns {Promise<{ fileName: string, text: string }[]>}
 */
async function probeDataSeries(logicalOrigin, origin, parseCsvTelemetryText) {
  const single = await trySingleDataCsv(logicalOrigin, origin, parseCsvTelemetryText);
  if (single) return [single];

  const out = [];
  let missesSinceOk = 0;
  const maxAfterFound = 5;
  const maxMissesBeforeAnyFound = 40;
  const maxIndex = 199;

  for (let i = 0; i <= maxIndex; i += 1) {
    const fileName = `data_${String(i).padStart(3, '0')}.csv`;
    let item;
    try {
      item = await fetchCsvIfValid(logicalOrigin, origin, fileName, parseCsvTelemetryText);
    } catch {
      item = null;
    }
    if (item) {
      out.push(item);
      missesSinceOk = 0;
    } else {
      missesSinceOk += 1;
      if (out.length > 0 && missesSinceOk >= maxAfterFound) break;
      if (out.length === 0 && missesSinceOk >= maxMissesBeforeAnyFound) break;
    }
  }
  return out;
}

/**
 * @param {string} resolvedOrigin logical
 * @param {string[]} fileNames
 * @param {(t: string) => unknown[]} parseCsvTelemetryText
 * @param {(i: {
 *   current: number,
 *   total: number,
 *   fileName: string,
 *   phase?: 'fetching' | 'receiving',
 *   receivedBytes?: number,
 *   totalBytes?: number,
 *   ok?: boolean,
 * }) => void} [onProgress]
 *   Fires when a file fetch starts (`phase: 'fetching'`), while bytes stream (`receiving`), and when done (`ok`).
 * @returns {Promise<{ fileName: string, text: string }[]>}
 */
export async function downloadDataloggerCsvsByName(
  resolvedOrigin,
  fileNames,
  parseCsvTelemetryText,
  onProgress
) {
  if (!resolvedOrigin || !fileNames?.length) return [];
  const origin = getDataloggerFetchBase(resolvedOrigin);
  const out = [];
  const total = fileNames.length;
  for (let i = 0; i < fileNames.length; i += 1) {
    const fileName = fileNames[i];
    onProgress?.({ current: i + 1, total, fileName, phase: 'fetching' });
    let lastStreamReport = 0;
    const text = await downloadDataloggerCsvText(resolvedOrigin, origin, fileName, {
      headersTimeoutMs: 35000,
      maxReadMs: 240000,
      onDownloadProgress: ({ receivedBytes, totalBytes }) => {
        const now = Date.now();
        const done = typeof totalBytes === 'number' && totalBytes > 0 && receivedBytes >= totalBytes;
        if (!done && now - lastStreamReport < 180) return;
        lastStreamReport = now;
        onProgress?.({
          current: i + 1,
          total,
          fileName,
          phase: 'receiving',
          receivedBytes,
          totalBytes,
        });
      },
    });
    if (!text) {
      onProgress?.({ current: i + 1, total, fileName, ok: false });
      continue;
    }
    const rows = parseCsvTelemetryText(text);
    if (!rows?.length) {
      onProgress?.({ current: i + 1, total, fileName, ok: false });
      continue;
    }
    out.push({ fileName, text });
    onProgress?.({ current: i + 1, total, fileName, ok: true });
  }
  return out;
}

/**
 * @param {string} resolvedOrigin logical
 * @param {(t: string) => unknown[]} parseCsvTelemetryText
 * @returns {Promise<{ fileName: string, text: string }[]>}
 */
export async function discoverDataloggerCsvs(resolvedOrigin, parseCsvTelemetryText) {
  if (!resolvedOrigin) return [];
  const base = getDataloggerFetchBase(resolvedOrigin);

  const names = await listDataloggerCsvFilenames(resolvedOrigin);
  if (names.length > 0) {
    const out = [];
    for (const fileName of names) {
      const item = await fetchCsvIfValid(resolvedOrigin, base, fileName, parseCsvTelemetryText);
      if (item) out.push(item);
    }
    if (out.length > 0) return out;
  }

  return probeDataSeries(resolvedOrigin, base, parseCsvTelemetryText);
}
